import { useTranslate } from '@refinedev/core';
import { Tag, Tooltip } from 'antd';
import type {
  CycleCloseReason,
  CycleKind,
  CycleStatus,
  RoyaltyReportStatus,
} from '@/api/generated/schemas';
import {
  CYCLE_CLOSE_REASON_COLORS,
  CYCLE_KIND_COLORS,
  CYCLE_STATUS_COLORS,
  ROYALTY_REPORT_STATUS_COLORS,
} from './colors';

// ─── CycleKindTag ─────────────────────────────────────────────────────────────

export const CycleKindTag = ({ value }: { value: CycleKind }) => {
  const t = useTranslate();
  return (
    <Tooltip title={t(`settlement.cycles.hints.kind_${value}`)}>
      <Tag color={CYCLE_KIND_COLORS[value] ?? 'default'}>
        {t(`settlement.cycles.kind.${value}`)}
      </Tag>
    </Tooltip>
  );
};

// ─── CycleStatusTag ───────────────────────────────────────────────────────────

/**
 * A live layer (`pending` / `active`) is tagged by its window state; a closed
 * one shows why it died instead of the bare "closed".
 */
export const CycleStatusTag = ({
  status,
  closeReason,
}: {
  status: CycleStatus;
  closeReason?: CycleCloseReason | null;
}) => {
  const t = useTranslate();

  if (status !== 'closed') {
    return (
      <Tooltip title={t(`settlement.cycles.hints.status_${status}`)}>
        <Tag color={CYCLE_STATUS_COLORS[status] ?? 'default'}>
          {t(`settlement.cycles.status.${status}`)}
        </Tag>
      </Tooltip>
    );
  }

  const key = closeReason ?? 'expired';
  return (
    <Tooltip title={t('settlement.cycles.status.closed')}>
      <Tag color={CYCLE_CLOSE_REASON_COLORS[key] ?? 'default'}>
        {t(`settlement.cycles.close_reason.${key}`)}
      </Tag>
    </Tooltip>
  );
};

// ─── RoyaltyReportStatusTag ───────────────────────────────────────────────────

export const RoyaltyReportStatusTag = ({
  status,
}: {
  status: RoyaltyReportStatus;
}) => {
  const t = useTranslate();
  return (
    <Tooltip title={t(`settlement.royalty_reports.hints.status_${status}`)}>
      <Tag color={ROYALTY_REPORT_STATUS_COLORS[status] ?? 'default'}>
        {t(`settlement.royalty_reports.status.${status}`)}
      </Tag>
    </Tooltip>
  );
};
